import React from 'react';
import { Table, TableBody, TableHead, TableRow, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

interface QuarterlyData {
  sendingDo: string;
  receivingDo: string;
  accountingQuarter: string;
  normalWeightA: string;
  normalWeightD: string;
  normalTotalWeight: string;
  normalItemsA: string;
  normalItemsD: string;
  normalTotalItems: string;
  bulkyWeightA: string;
  bulkyWeightD: string;
  bulkyTotalWeight: string;
  bulkyItemsA: string;
  bulkyItemsD: string;
  bulkyTotalItems: string;
}

interface TotalData {
  normalWeightA: string;
  normalWeightD: string;
  normalTotalWeight: string;
  normalItemsA: string;
  normalItemsD: string;
  normalTotalItems: string;
  bulkyWeightA: string;
  bulkyWeightD: string;
  bulkyTotalWeight: string;
  bulkyItemsA: string;
  bulkyItemsD: string;
  bulkyTotalItems: string;
}

interface IbrsItemAndWeight {
  quarterOneIbrsItemAndWeight: QuarterlyData;
  quarterTwoIbrsItemAndWeight: QuarterlyData;
  quarterThreeIbrsItemAndWeight: QuarterlyData;
  quarterFourIbrsItemAndWeight: QuarterlyData;
  totalIbrsItemAndWeightItem: TotalData;
}

interface CN19ItemsWeightsTableProps {
  title?: string;
  data: IbrsItemAndWeight;
  type?: 'inbound' | 'outbound';
}

// Styled components
const StyledTableContainer = styled('div')(() => ({
  overflowX: 'auto',
  border: '1px solid #E1E8ED',
  borderRadius: '8px',
  backgroundColor: '#FFFFFF',
  marginBottom: '24px',
}));

const StyledTable = styled(Table)(() => ({
  minWidth: 900,
}));

const HeaderCell = styled('th')(({ theme }) => ({
  fontWeight: 700,
  fontSize: '12px',
  backgroundColor: '#E7EFF5 !important',
  textAlign: 'center',
  color: '#063142',
  padding: theme.spacing(1, 1.5),
  position: 'sticky',
  top: 0,
  zIndex: 1300,
  border: '1px solid #D1D9E0',
}));

const SubHeaderCell = styled('th')(({ theme }) => ({
  fontWeight: 600,
  fontSize: '11px',
  backgroundColor: '#E7EFF5 !important',
  textAlign: 'center',
  color: '#063142',
  padding: theme.spacing(0.75, 1),
  position: 'sticky',
  top: '30px',
  zIndex: 1299,
  border: '1px solid #D1D9E0',
}));

const SubSubHeaderCell = styled('th')(({ theme }) => ({
  fontWeight: 600,
  fontSize: '10px',
  backgroundColor: '#E7EFF5 !important',
  textAlign: 'center',
  color: '#063142',
  padding: theme.spacing(0.5, 0.75),
  position: 'sticky',
  top: '60px',
  zIndex: 1298,
  border: '1px solid #D1D9E0',
}));

const DataCell = styled('td')(({ theme }) => ({
  fontSize: '11px',
  textAlign: 'center',
  backgroundColor: '#FFFFFF',
  padding: theme.spacing(0.75, 1),
  border: '1px solid #E1E8ED',
  '&.number-cell': {
    fontFamily: 'monospace',
    textAlign: 'right',
    fontWeight: 500,
  },
  '&.label-cell': {
    textAlign: 'center',
    fontWeight: 600,
  },
}));

const TotalCell = styled('td')(({ theme }) => ({
  fontSize: '11px',
  textAlign: 'center',
  backgroundColor: '#FFF3E0',
  padding: theme.spacing(0.75, 1),
  border: '1px solid #D1D9E0',
  fontWeight: 700,
  '&.number-cell': {
    fontFamily: 'monospace',
    textAlign: 'right',
    fontWeight: 700,
  },
}));

function CN19ItemsWeightsTable({ title, data, type }: CN19ItemsWeightsTableProps): React.ReactElement {
  // Display raw string value as received from backend
  const formatValue = (value: string | number): string => {
    if (!value && value !== 0) return '';
    if (value === '-') return '-';
    return String(value);
  };

  // Extract quarterly data
  const quarters = [
    { key: 'quarterOneIbrsItemAndWeight', label: '1', data: data.quarterOneIbrsItemAndWeight },
    { key: 'quarterTwoIbrsItemAndWeight', label: '2', data: data.quarterTwoIbrsItemAndWeight },
    { key: 'quarterThreeIbrsItemAndWeight', label: '3', data: data.quarterThreeIbrsItemAndWeight },
    { key: 'quarterFourIbrsItemAndWeight', label: '4', data: data.quarterFourIbrsItemAndWeight },
  ];

  // Determine background color based on type
  const getBackgroundColor = () => {
    if (type === 'inbound') return 'rgb(230, 245, 230)';
    if (type === 'outbound') return 'rgb(255, 240, 240)';
    return '#FFFFFF';
  };

  return (
    <Box sx={{ mb: 4 }} data-testid="cn19-items-weights-table-container">
      <Typography variant='h6' sx={{ fontWeight: 700, color: '#063142', mb: 3 }} data-testid="cn19-items-weights-table-title">
        {title}
      </Typography>
      <StyledTableContainer data-testid="cn19-items-weights-table-wrapper">
        <StyledTable data-testid="cn19-items-weights-table">
          <TableHead>
            {/* Level 1 Headers - Main Groups */}
            <TableRow>
              <HeaderCell rowSpan={3} style={{ minWidth: '120px' }}>
                Sending DO
              </HeaderCell>
              <HeaderCell rowSpan={3} style={{ minWidth: '120px' }}>
                Receiving DO
              </HeaderCell>
              <HeaderCell rowSpan={3} style={{ minWidth: '80px' }}>
                Quarter
              </HeaderCell>
              <HeaderCell colSpan={6}>Normal IBRS</HeaderCell>
              <HeaderCell colSpan={6}>Bulky IBRS</HeaderCell>
            </TableRow>

            {/* Level 2 Headers - Weight/Items */}
            <TableRow>
              <SubHeaderCell colSpan={3}>Weight (kg)</SubHeaderCell>
              <SubHeaderCell colSpan={3}>Items</SubHeaderCell>
              <SubHeaderCell colSpan={3}>Weight (kg)</SubHeaderCell>
              <SubHeaderCell colSpan={3}>Items</SubHeaderCell>
            </TableRow>

            {/* Level 3 Headers - A/D/Total */}
            <TableRow>
              <SubSubHeaderCell>A</SubSubHeaderCell>
              <SubSubHeaderCell>D</SubSubHeaderCell>
              <SubSubHeaderCell>Total</SubSubHeaderCell>
              <SubSubHeaderCell>A</SubSubHeaderCell>
              <SubSubHeaderCell>D</SubSubHeaderCell>
              <SubSubHeaderCell>Total</SubSubHeaderCell>
              <SubSubHeaderCell>A</SubSubHeaderCell>
              <SubSubHeaderCell>D</SubSubHeaderCell>
              <SubSubHeaderCell>Total</SubSubHeaderCell>
              <SubSubHeaderCell>A</SubSubHeaderCell>
              <SubSubHeaderCell>D</SubSubHeaderCell>
              <SubSubHeaderCell>Total</SubSubHeaderCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Quarterly Data Rows */}
            {quarters.map((quarter, index) => (
              <TableRow key={quarter.key}>
                <DataCell className='label-cell' style={{ backgroundColor: getBackgroundColor() }}>{quarter.data.sendingDo}</DataCell>
                <DataCell className='label-cell' style={{ backgroundColor: getBackgroundColor() }}>{quarter.data.receivingDo}</DataCell>
                <DataCell className='label-cell' style={{ backgroundColor: getBackgroundColor() }}>{quarter.label}</DataCell>

                {/* Normal IBRS Weight */}
                <DataCell className='number-cell' style={{ backgroundColor: getBackgroundColor() }}>
                  {formatValue(quarter.data.normalWeightA)}
                </DataCell>
                <DataCell className='number-cell' style={{ backgroundColor: getBackgroundColor() }}>
                  {formatValue(quarter.data.normalWeightD)}
                </DataCell>
                <DataCell className='number-cell' style={{ backgroundColor: getBackgroundColor() }}>
                  {formatValue(quarter.data.normalTotalWeight)}
                </DataCell>

                {/* Normal IBRS Items */}
                <DataCell className='number-cell' style={{ backgroundColor: getBackgroundColor() }}>
                  {formatValue(quarter.data.normalItemsA)}
                </DataCell>
                <DataCell className='number-cell' style={{ backgroundColor: getBackgroundColor() }}>
                  {formatValue(quarter.data.normalItemsD)}
                </DataCell>
                <DataCell className='number-cell' style={{ backgroundColor: getBackgroundColor() }}>
                  {formatValue(quarter.data.normalTotalItems)}
                </DataCell>

                {/* Bulky IBRS Weight */}
                <DataCell className='number-cell' style={{ backgroundColor: getBackgroundColor() }}>
                  {formatValue(quarter.data.bulkyWeightA)}
                </DataCell>
                <DataCell className='number-cell' style={{ backgroundColor: getBackgroundColor() }}>
                  {formatValue(quarter.data.bulkyWeightD)}
                </DataCell>
                <DataCell className='number-cell' style={{ backgroundColor: getBackgroundColor() }}>
                  {formatValue(quarter.data.bulkyTotalWeight)}
                </DataCell>

                {/* Bulky IBRS Items */}
                <DataCell className='number-cell' style={{ backgroundColor: getBackgroundColor() }}>
                  {formatValue(quarter.data.bulkyItemsA)}
                </DataCell>
                <DataCell className='number-cell' style={{ backgroundColor: getBackgroundColor() }}>
                  {formatValue(quarter.data.bulkyItemsD)}
                </DataCell>
                <DataCell className='number-cell' style={{ backgroundColor: getBackgroundColor() }}>
                  {formatValue(quarter.data.bulkyTotalItems)}
                </DataCell>
              </TableRow>
            ))}

            {/* Total Row */}
            <TableRow>
              <TotalCell colSpan={3} style={{ textAlign: 'center', fontWeight: 700 }}>
                Yearly Total
              </TotalCell>

              {/* Normal IBRS Weight Totals */}
              <TotalCell className='number-cell'>
                {formatValue(data.totalIbrsItemAndWeightItem.normalWeightA)}
              </TotalCell>
              <TotalCell className='number-cell'>
                {formatValue(data.totalIbrsItemAndWeightItem.normalWeightD)}
              </TotalCell>
              <TotalCell className='number-cell'>
                {formatValue(data.totalIbrsItemAndWeightItem.normalTotalWeight)}
              </TotalCell>

              {/* Normal IBRS Items Totals */}
              <TotalCell className='number-cell'>
                {formatValue(data.totalIbrsItemAndWeightItem.normalItemsA)}
              </TotalCell>
              <TotalCell className='number-cell'>
                {formatValue(data.totalIbrsItemAndWeightItem.normalItemsD)}
              </TotalCell>
              <TotalCell className='number-cell'>
                {formatValue(data.totalIbrsItemAndWeightItem.normalTotalItems)}
              </TotalCell>

              {/* Bulky IBRS Weight Totals */}
              <TotalCell className='number-cell'>
                {formatValue(data.totalIbrsItemAndWeightItem.bulkyWeightA)}
              </TotalCell>
              <TotalCell className='number-cell'>
                {formatValue(data.totalIbrsItemAndWeightItem.bulkyWeightD)}
              </TotalCell>
              <TotalCell className='number-cell'>
                {formatValue(data.totalIbrsItemAndWeightItem.bulkyTotalWeight)}
              </TotalCell>

              {/* Bulky IBRS Items Totals */}
              <TotalCell className='number-cell'>
                {formatValue(data.totalIbrsItemAndWeightItem.bulkyItemsA)}
              </TotalCell>
              <TotalCell className='number-cell'>
                {formatValue(data.totalIbrsItemAndWeightItem.bulkyItemsD)}
              </TotalCell>
              <TotalCell className='number-cell'>
                {formatValue(data.totalIbrsItemAndWeightItem.bulkyTotalItems)}
              </TotalCell>
            </TableRow>
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
    </Box>
  );
}

export default CN19ItemsWeightsTable;
